import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { SYSCOHADA_CLASSES } from '../../../types/dashboard-widgets';
import { ResponsiveAmount, AccountCodeLink } from '../../ui/ResponsiveAmount';

interface AccountWidgetProps {
  config: {
    accountCode?: string;
    accountClass?: number;
    showTransactions?: boolean;
  };
  data: any;
}

// Récupérer les données de compte depuis les données backend
const getAccountData = (data: any, accountCode?: string, accountClass?: number) => {
  // Si on a les topAccounts dans les données
  if (data?.topAccounts && Array.isArray(data.topAccounts)) {
    if (accountCode) {
      const account = data.topAccounts.find((a: any) => a.code === accountCode);
      if (account) return account;
    }
    if (accountClass) {
      const accounts = data.topAccounts.filter((a: any) => 
        a.code?.startsWith(String(accountClass))
      );
      // Trouver le nom de classe correct depuis le tableau SYSCOHADA_CLASSES
      const classInfo = SYSCOHADA_CLASSES.find(c => c.code === String(accountClass));
      return {
        isClass: true,
        className: classInfo?.name || `Classe ${accountClass}`,
        accounts,
        totalBalance: accounts.reduce((sum: number, a: any) => sum + (a.balance || 0), 0)
      };
    }
  }
  
  // Données par défaut si pas de données backend
  return {
    code: accountCode || '571',
    name: accountCode ? `Compte ${accountCode}` : 'Caisse',
    balance: 0,
    debit: 0,
    credit: 0,
    movements: 0
  };
};

export function AccountWidget({ config, data }: AccountWidgetProps) {
  const accountData = getAccountData(data, config.accountCode, config.accountClass);

  // Affichage pour une classe de comptes
  if (accountData.isClass) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-border gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                Classe {config.accountClass}
              </p>
              <AccountCodeLink 
                code={String(config.accountClass)} 
                variant="subtle"
                showIcon
              />
            </div>
            <p className="text-sm font-medium truncate">{accountData.className}</p>
          </div>
          <ResponsiveAmount 
            value={accountData.totalBalance}
            size="lg"
            className="font-bold flex-shrink-0"
            colorize
          />
        </div>
        
        <div className="space-y-1.5 max-h-32 overflow-y-auto">
          {accountData.accounts.length > 0 ? (
            accountData.accounts.slice(0, 5).map((acc: any, idx: number) => (
              <div key={idx} className="flex items-center justify-between text-sm gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <AccountCodeLink 
                    code={acc.code} 
                    accountId={acc.id}
                    variant="badge"
                  />
                  <span className="truncate text-muted-foreground">{acc.name}</span>
                </div>
                <ResponsiveAmount 
                  value={acc.balance || 0}
                  size="sm"
                  colorize
                  className="flex-shrink-0"
                />
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-2">Aucun compte</p>
          )}
        </div>
        
        {accountData.accounts.length > 5 && (
          <p className="text-xs text-muted-foreground text-center">
            +{accountData.accounts.length - 5} autres comptes
          </p>
        )}
      </div>
    );
  }

  // Affichage pour un compte individuel
  const balance = accountData.balance || 0;
  const debit = accountData.debit || 0;
  const credit = accountData.credit || 0;
  const movements = accountData.movements || 0;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <AccountCodeLink 
              code={accountData.code}
              accountId={accountData.id}
              variant="badge"
              showIcon
            />
            {movements > 0 && (
              <span className="text-xs text-muted-foreground">
                {movements} mvt{movements > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <p className="text-sm font-medium mt-1 truncate">{accountData.name}</p>
        </div>
      </div>

      {/* Balance */}
      <div className="p-3 rounded-lg bg-muted/50 border border-border/50">
        <p className="text-xs text-muted-foreground mb-1">Solde actuel</p>
        <div className="flex items-baseline gap-2 flex-wrap">
          <ResponsiveAmount 
            value={balance}
            size="xl"
            className="font-bold"
            colorize
          />
          {balance !== 0 && (
            balance >= 0 
              ? <TrendingUp className="h-4 w-4 text-green-600 flex-shrink-0" />
              : <TrendingDown className="h-4 w-4 text-red-600 flex-shrink-0" />
          )}
        </div>
      </div>

      {/* Debit/Credit */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="p-2 rounded bg-blue-50 dark:bg-blue-900/20">
          <p className="text-xs text-blue-600 dark:text-blue-400">Débit</p>
          <ResponsiveAmount 
            value={debit}
            size="sm"
            className="font-semibold text-blue-700 dark:text-blue-300"
            showCurrency={false}
          />
        </div>
        <div className="p-2 rounded bg-purple-50 dark:bg-purple-900/20">
          <p className="text-xs text-purple-600 dark:text-purple-400">Crédit</p>
          <ResponsiveAmount 
            value={credit}
            size="sm"
            className="font-semibold text-purple-700 dark:text-purple-300"
            showCurrency={false}
          />
        </div>
      </div>
    </div>
  );
}
